import { useLocation, useNavigate } from 'react-router-dom'
import { PageTitle, TabBody } from '../../components/PageTitle'
import { CheckInPage } from './CheckInPage'
import { DeviceListPage } from './DeviceListPage'
import { EnrollmentPage } from './EnrollmentPage'
import { TrustPage } from '../trust/TrustPage'
import { withTab } from '../../lib/tabs'
import { activeTabFor, screenFor, tabsFor } from '../../lib/screens'
import { session } from '../../lib/api'

/**
 * The fleet, and the three things that decide what reaches it.
 *
 * Four tabs rather than three, and the fourth is a split rather than an addition. The screen used to
 * be Devices, Deployment and Trust, and the first of those had become the place that everything
 * fleet-shaped landed: the device list, plus device seats, plus the enrollment tokens, plus the
 * credential lifetime. It measured eleven thousand pixels, and the table the screen is named after
 * started six thousand nine hundred pixels down it - past four cards an operator opens at setup and
 * rarely again. Trust, meanwhile, was one screen exactly. Three tabs carrying fourteen screens, five
 * screens and one is not three tabs; it is a list with two appendices.
 *
 * So each tab answers one question, and the cards moved to the question they answer rather than to the
 * screen they were first written for. Seats sit with the kit that carries the seat count. Tokens sit
 * with the kit that issues one. The credential lifetime sits with the check-in cadence, because a
 * credential rotates only at a check-in and the two numbers multiply into one answer.
 *
 * **The tab keys did not change with the labels**, and that is deliberate rather than untidy.
 * `/deployment` and `/trust` redirect onto `?tab=deployment` and `?tab=trust`, `layout.spec.ts` drives
 * that table, and the redirects are kept indefinitely because bookmarks exist. Renaming the key to
 * match the word on the button would have broken every link anybody has pasted into a ticket in order
 * to tidy a query string nobody reads (D-429).
 */
const SCREEN = screenFor('/devices')

export function DevicesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const roles = session.identity()?.roles ?? []
  const tabs = tabsFor(SCREEN, roles)
  const tab = activeTabFor(SCREEN, location.search, roles)

  return (
    <div className="tabbed">
      {/*
        The host owns the page's one h1, and now owns the only title on the screen. Each body used to
        render a PageTitle as well, so the Fleet tab put "Devices" in an h1, a tab strip under it, and
        "Devices" again in an h2 - two headings, two different descriptions, one screen. The tab strip
        already names the panel, so the panel is labeled by its tab rather than by a repeat of it, which
        is both the standard pattern and one heading fewer to disagree with itself (D-429).
      */}
      <div className="page-header">
        <div>
          <PageTitle>Devices</PageTitle>
          <p>Every machine running the agent, how one gets there, and what it has to trust.</p>
        </div>
      </div>

      <div className="tab-strip" role="tablist" aria-label={SCREEN.title}>
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`devices-tab-${key}`}
            aria-selected={tab === key}
            aria-controls="devices-tab-panel"
            onClick={() =>
              navigate(`/devices${key === SCREEN.defaultTab ? '' : withTab(location.search, key)}`)
            }
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div id="devices-tab-panel" role="tabpanel" aria-labelledby={`devices-tab-${tab}`} tabIndex={-1}>
        <TabBody>
          {tab === 'deployment' ? (
            <EnrollmentPage />
          ) : tab === 'checkin' ? (
            <CheckInPage />
          ) : tab === 'trust' ? (
            <TrustPage />
          ) : (
            <DeviceListPage />
          )}
        </TabBody>
      </div>
    </div>
  )
}
